import * as React from 'react';
import { SearchableMultiSelect, SelectOption } from '@/shared/components/forms';

interface AdminJobSelectProps {
  /** Options as { value: jobId, label: "Practice — Title (#id)" } */
  options: SelectOption<number>[];
  /** Form param the selection submits as, e.g. "job_ids[]" */
  fieldName: string;
  initialSelected?: number[];
  isMulti?: boolean;
  placeholder?: string;
  helpText?: string;
  inputId?: string;
}

/**
 * AdminJobSelect - searchable job picker for admin ERB forms.
 *
 * Wraps SearchableMultiSelect and mirrors the selection into hidden inputs
 * so a plain (non-React) form POST submits it like a native select. The
 * dropdown renders in a body-level portal so it is never clipped by
 * Bootstrap modals.
 */
export const AdminJobSelect: React.FC<AdminJobSelectProps> = ({
  options,
  fieldName,
  initialSelected = [],
  isMulti = true,
  placeholder = 'Search jobs by practice or title…',
  helpText,
  inputId,
}) => {
  const [selected, setSelected] = React.useState<number[] | number | null>(
    isMulti ? initialSelected : initialSelected[0] ?? null
  );

  const selectedIds: number[] = isMulti
    ? (selected as number[]) ?? []
    : selected == null
      ? []
      : [selected as number];

  return (
    <div data-testid="admin-job-select">
      <SearchableMultiSelect<number>
        options={options}
        value={selected}
        onChange={setSelected}
        isMulti={isMulti}
        placeholder={placeholder}
        helpText={helpText}
        id={inputId}
        menuPortalTarget={typeof document === 'undefined' ? null : document.body}
        customStyles={{
          // Above Bootstrap's modal (z-index 1055)
          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
        }}
      />
      {selectedIds.map((id) => (
        <input key={id} type="hidden" name={fieldName} value={id} />
      ))}
    </div>
  );
};

export default AdminJobSelect;
